import type { PageDesignSettings } from './pageDesign';

/**
 * Margin settings types for document export
 */

/**
 * Margin settings stored in centimeters (cm)
 * These values are applied to both DOCX and HWPX exports
 */
export interface MarginSettings {
  /** Optional native header/footer design. Missing means unchanged legacy layout. */
  pageDesign?: PageDesignSettings;
  /** Top margin in cm (상) */
  top: number;
  /** Bottom margin in cm (하) */
  bottom: number;
  /** Left margin in cm (좌) - becomes "안쪽" (inside) when mirrorMargins is true */
  left: number;
  /** Right margin in cm (우) - becomes "바깥쪽" (outside) when mirrorMargins is true */
  right: number;
  /** Column spacing in cm (2단 사이 간격) */
  colSpacing: number;
  /** Enable mirror margins for odd/even page swapping (대칭 여백) */
  mirrorMargins: boolean;
  /** Number of columns (1 or 2) */
  columnCount: 1 | 2;
  /**
   * Line between two columns (2단 구분선). Optional: settings and presets saved before the
   * field existed stay valid, and a missing value means a line; only false turns it off.
   * clampMarginSettings fills it in at the boundary. Kept when the page goes back to one
   * column (SPEC-ONECLICK-FORMAT-001 REQ-OCF-014, REQ-OCF-020).
   */
  columnSeparator?: boolean;
}

/**
 * Default margin values (matching desktop app defaults)
 */
export const DEFAULT_MARGIN_SETTINGS: MarginSettings = {
  top: 1.5,
  bottom: 1.6,
  left: 1.9,
  right: 1.9,
  colSpacing: 1.27,
  mirrorMargins: false,
  columnCount: 1,
  columnSeparator: true,
};

/**
 * Margin constraints for validation
 */
export const MARGIN_CONSTRAINTS = {
  /** Minimum margin value in cm */
  MIN: 0.5,
  /** Maximum margin value in cm */
  MAX: 5.0,
  /** Minimum column spacing in cm */
  COL_SPACING_MIN: 0.3,
  /** Maximum column spacing in cm */
  COL_SPACING_MAX: 3.0,
};

/**
 * LocalStorage key for margin settings
 */
export const MARGIN_SETTINGS_STORAGE_KEY = 'autoqm_margin_settings';

/**
 * LocalStorage key for margin presets
 */
export const MARGIN_PRESETS_STORAGE_KEY = 'autoqm_margin_presets';

/**
 * A saved preset containing margin settings with a name
 */
export interface MarginPreset {
  /** Unique identifier for the preset */
  id: string;
  /** User-defined name for the preset */
  name: string;
  /** The margin settings stored in this preset */
  settings: MarginSettings;
  /** Timestamp when the preset was created */
  createdAt: number;
}

/**
 * Maximum number of margin presets
 */
export const MAX_MARGIN_PRESETS = 10;

/**
 * Context value type for margin settings
 */
export interface MarginSettingsContextValue {
  /** Current margin settings */
  settings: MarginSettings;
  /** Server sync timestamp */
  updatedAt: number;
  /** Update margin settings */
  setSettings: (settings: Partial<MarginSettings>) => void;
  /** Reset to default settings */
  resetSettings: () => void;
  /** Validate a single margin value */
  validateMarginValue: (value: number, isColSpacing?: boolean) => number;
  /** List of saved presets */
  presets: MarginPreset[];
  /** Save current settings as a new preset */
  savePreset: (name: string) => void;
  /** Load a preset by ID */
  loadPreset: (id: string) => void;
  /** Delete a preset by ID */
  deletePreset: (id: string) => void;
  /** Rename a preset */
  renamePreset: (id: string, newName: string) => void;
  /** Whether server sync is in progress */
  isSyncing: boolean;
  /** Whether initial hydration from server is in progress */
  isHydrating: boolean;
  syncError: string | null;
  loadFailed: boolean;
  retrySync: () => void;
}
